import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navTextClass = isScrolled
    ? 'text-foreground-900'
    : 'text-white';

  const navBorderClass = isScrolled
    ? 'border-foreground-300 text-foreground-700 hover:border-foreground-500 bg-background-50'
    : 'border-white/40 text-white hover:border-white/80 bg-white/10';

  const navPillClass = isScrolled
    ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
    : 'border-white/50 text-white hover:bg-white/15';

  const navCtaClass = isScrolled
    ? 'bg-primary-500 text-white hover:bg-primary-600'
    : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-foreground-200/50 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap transition-colors duration-300 cursor-pointer ${
              isScrolled ? 'text-foreground-900 hover:text-primary-600' : 'text-white hover:text-white/80'
            }`}
          >
            USFans Spreadsheet
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${
                isScrolled
                  ? 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              Strona główna
            </Link>

            <Link
              to="/artykul/usfans-spreadsheet-poradnik-zakupowy"
              className={`px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${navPillClass}`}
            >
              Poradnik zakupowy
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${navBorderClass}`}
              >
                <span>Wybierz kategorię</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-2xl border border-background-200 shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden animate-fade-in">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                      >
                        {cat.namePl}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${navPillClass}`}
            >
              usfans spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 ${navCtaClass}`}
            >
              Kup teraz
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${
              isScrolled
                ? 'text-foreground-800 hover:bg-background-100'
                : 'text-white hover:bg-white/15'
            }`}
            aria-label="Menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto animate-fade-in">
          <div className="px-4 py-6 space-y-6">
            <div>
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-primary-600"></i>
                </div>
                <span className="font-semibold text-foreground-800">Strona główna</span>
              </Link>
            </div>

            <div>
              <Link
                to="/artykul/usfans-spreadsheet-poradnik-zakupowy"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 hover:bg-accent-50 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-accent-600"></i>
                </div>
                <span className="font-semibold text-foreground-800">Poradnik zakupowy</span>
              </Link>
            </div>

            <div>
              <h3 className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Kategorie produktów</h3>
              <div className="grid grid-cols-2 gap-2 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.namePl} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="text-sm font-medium text-foreground-700">{cat.namePl}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 px-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary-300 text-primary-600 font-medium text-sm hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                usfans spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i>
                Kup teraz
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}